import {
  CheckOutlined,
  ExportOutlined,
  FileImageOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Markable } from '@medcom/shared-ui/components/Markable.tsx';
import { filterByTokens } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Input, Table, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useDebounce } from 'use-debounce';
import { MainContent } from '../../../components/MainContent';
import { TestTubeColor } from '../../../components/TestTubeColor';
import type { TestTubesQuery } from '../../../graphql/generated/graphql.ts';
import { tableActionCell } from '../../../styles/globalCss';
import { testTubeColorProperties, translateTestTubeSupplier } from '../../../utils/enumHelpers';
import { openExternalLink } from '../../../utils/linkOpener.ts';
import { TEST_TUBE_QUERY } from './TestTubes.gql';
import { CreateTestTubeModal } from './testTubes/CreateTestTubeModal';
import { TestTubeImageModal } from './testTubes/TestTubeImageModal';
import { UpdateTestTubeModal } from './testTubes/UpdateTestTubeModal';

const { useToken } = theme;

export type TestTube = NonNullable<TestTubesQuery['labTestTubes']>[number];

export const TestTubes: React.FC = () => {
  const { token } = useToken();
  const { id: labId } = useParams<{ id: string }>();
  const [search, setSearch] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 250);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editTestTube, setEditTestTube] = useState<TestTube | null>(null);
  const [imageTestTube, setImageTestTube] = useState<TestTube | null>(null);

  const { data, loading, refetch } = useQuery(TEST_TUBE_QUERY, {
    variables: {
      labId: labId ?? '',
    },
    pollInterval: 60000,
  });

  const columns: ColumnsType<TestTube> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: 'ascend',
      ellipsis: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Lieferant',
      dataIndex: 'supplier',
      key: 'supplier',
      ellipsis: true,
      sorter: (a, b) => a.supplier.localeCompare(b.supplier),
      render: value => translateTestTubeSupplier(value),
    },
    {
      title: 'Etiketten-Text',
      dataIndex: 'labelText',
      key: 'labelText',
      ellipsis: true,
      sorter: (a, b) => a.labelText.localeCompare(b.labelText),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Etiketten-Text 2',
      dataIndex: 'labelText2',
      key: 'labelText2',
      ellipsis: true,
      sorter: (a, b) => a.labelText2.localeCompare(b.labelText2),
      render: value => <Markable tokens={debouncedSearch ?? ''}>{value}</Markable>,
    },
    {
      title: 'Farbe',
      dataIndex: 'color',
      key: 'color',
      ellipsis: true,
      sorter: (a, b) => a.color.localeCompare(b.color),
      render: value => <TestTubeColor properties={testTubeColorProperties(value)} />,
    },
    {
      title: 'Volumen',
      dataIndex: 'volume',
      key: 'volume',
      ellipsis: true,
      sorter: (a, b) => a.volume - b.volume,
    },
    {
      title: 'Spez. Drucker',
      dataIndex: 'specialPrinterRequired',
      key: 'specialPrinterRequired',
      ellipsis: true,
      width: 130,
      sorter: (a, b) => Number(a.specialPrinterRequired) - Number(b.specialPrinterRequired),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      width: 80,
      render: value => {
        if (!value) {
          return;
        }
        return (
          <Button
            type="link"
            className={css`
              height: 20px;
              margin: 0;
              padding: 0;
            `}
            icon={<ExportOutlined />}
            onClick={e => {
              e.stopPropagation();
              openExternalLink(value);
            }}
          />
        );
      },
    },
    {
      title: 'Bild',
      dataIndex: 'hasImage',
      key: 'hasImage',
      ellipsis: true,
      sorter: (a, b) => Number(a.hasImage) - Number(b.hasImage),
      render: value => (value ? <CheckOutlined /> : ''),
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      ellipsis: true,
      width: '50px',
      className: tableActionCell,
      render: (_, record) => (
        <Tooltip title="Bild ändern">
          <Button
            icon={<FileImageOutlined />}
            type="text"
            onClick={e => {
              e.stopPropagation();
              setImageTestTube(record);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const filteredTestTubes: TestTube[] = useMemo(
    () => filterByTokens(data?.labTestTubes ?? [], debouncedSearch, [t => t.name, t => t.labelText, t => t.labelText2]),
    [debouncedSearch, data]
  );

  return (
    <MainContent>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: token.paddingSM }}>
        <Input
          allowClear
          autoFocus
          placeholder="Suche"
          value={search ?? ''}
          onChange={e => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            <Tooltip title="Suche nach Name, Etiketten-Text oder Etiketten-Text 2">
              <InfoCircleOutlined />
            </Tooltip>
          }
          style={{ width: '300px', marginRight: token.paddingSM }}
        />
        <Button type="primary" icon={<PlusCircleOutlined />} disabled={loading} onClick={() => setCreateModalVisible(true)}>
          Neuer Probenbehälter
        </Button>
      </div>
      <Table<TestTube>
        scroll={{ x: 'max-content' }}
        rowKey={record => record.id}
        size="middle"
        showSorterTooltip={false}
        dataSource={filteredTestTubes}
        onRow={record => ({ onClick: () => setEditTestTube(record), style: { cursor: 'pointer' } })}
        pagination={{
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]} bis ${range[1]} von ${total} Probenbehältern`,
        }}
        loading={loading}
        columns={columns}
      />
      <CreateTestTubeModal
        modalVisible={createModalVisible}
        onSuccess={() => refetch()}
        onClose={() => setCreateModalVisible(false)}
        labId={labId ?? ''}
      />
      <UpdateTestTubeModal testTube={editTestTube} onSuccess={() => refetch()} onClose={() => setEditTestTube(null)} />
      <TestTubeImageModal
        testTube={imageTestTube}
        onClose={() => {
          refetch();
          setImageTestTube(null);
        }}
      />
    </MainContent>
  );
};
